import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { loginWithPhone as apiLoginWithPhone } from "../../services/api/auth"

interface AuthState {
  token: string | null
  phoneNumber: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const initialState: AuthState = {
  token: localStorage.getItem("auth_token"),
  phoneNumber: localStorage.getItem("phone_number"),
  isAuthenticated: !!localStorage.getItem("auth_token"),
  isLoading: false,
  error: null,
}

// Auth token is now managed by Redux state directly
const storedToken = localStorage.getItem("auth_token")

// Async thunk for login
export const loginWithPhone = createAsyncThunk(
  "auth/loginWithPhone",
  async (phoneNumber: string, { rejectWithValue }) => {
    try {
      // Use the API service for login
      const result = await apiLoginWithPhone(phoneNumber)
      
      // Extract token from different possible response formats
      const token = result.token || result.access_token || result.data?.token;
      
      if (token) {
        return { token, phoneNumber };
      } else {
        throw new Error('No token received from server');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
)

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null
      state.phoneNumber = null
      state.isAuthenticated = false
      state.error = null
      localStorage.removeItem("auth_token")
      localStorage.removeItem("phone_number")
      
      // Auth token management handled in slice
    },
    clearTokenAfterPayment: (state) => {
      // Clear token but keep authenticated state for UX
      state.token = null
      localStorage.removeItem("auth_token")
      
      // Auth token management handled in slice
            },
      clearError: (state) => {
        state.error = null
      },
      setToken: (state, action: PayloadAction<{ token: string; phoneNumber: string }>) => {
        state.token = action.payload.token
        state.phoneNumber = action.payload.phoneNumber
        state.isAuthenticated = true
        state.error = null
        localStorage.setItem("auth_token", action.payload.token)
        localStorage.setItem("phone_number", action.payload.phoneNumber)
      }
    },
    extraReducers: (builder) => {
      builder
        .addCase(loginWithPhone.pending, (state) => {
          state.isLoading = true
          state.error = null
        })
      .addCase(loginWithPhone.fulfilled, (state, action) => {
        state.isLoading = false
        state.token = action.payload.token
        state.phoneNumber = action.payload.phoneNumber
        state.isAuthenticated = true
        state.error = null
        
        // Save to localStorage
        localStorage.setItem("auth_token", action.payload.token)
        localStorage.setItem("phone_number", action.payload.phoneNumber)
        
        // Auth token is now handled by API client interceptor
      })
      .addCase(loginWithPhone.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.isAuthenticated = false
        state.token = null
        state.phoneNumber = null
      })
  },
})

export const { logout, clearError, setToken, clearTokenAfterPayment } = authSlice.actions
export const selectAuth = (state: { auth: AuthState }) => state.auth
export const selectToken = (state: { auth: AuthState }) => state.auth.token
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error

export default authSlice.reducer